import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import React, {
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Block, Edge } from 'src/types/shared/blocksV3';
import getAddableBlocks from 'src/utils/getAddableBlocks';
import { DeckContext } from '../../../../context/DeckContextV3';
import BlockType from 'src/enums/blockType';
import getBlock from 'src/utils/getBlock';
import handleAddLanding from '../../../../utils/addLanding';
import { getOppositeEdge } from '../../../../utils/edgeUtils';
import handleAddStair from '../../../../utils/addStair';
import handleAddBumpout from '../../../../utils/addBumpout';
import handleAddDeckbox from '../../../../utils/addDeckbox';

const BlockOptions: React.FC<{
	setStage: Dispatch<SetStateAction<number>>;
	handleCloseDialog: () => void;
	block: Block | null;
	edge: Edge | undefined;
}> = ({ setStage, handleCloseDialog, block, edge }) => {
	const { state, dispatch } = useContext(DeckContext);
	const theme = useTheme();

	const [addIntermidiateLanding, setAddIntermidiateLanding] = useState(false);
	const [addedStairId, setAddedStairId] = useState('');
	const [addedLandingId, setAddedLandingId] = useState('');

	const addableBlocks = getAddableBlocks(block);

	useEffect(() => {
		const addedStair = getBlock(state.blocks, addedStairId);

		if (addIntermidiateLanding && addedStair) {
			const blockAttachedEdge = addedStair.edges.find(
				(eg) => eg.attachedToParent
			);

			const intermediateLandingId = handleAddLanding({
				state,
				dispatch,
				block: addedStair,
				edge: addedStair.edges.find(
					(eg) => eg.label == getOppositeEdge(blockAttachedEdge?.label)
				),
				isIntermediate: true,
			});

			setAddedStairId('');
			setAddedLandingId(intermediateLandingId);
		}
	}, [state.blocks, addedStairId, addIntermidiateLanding]);

	useEffect(() => {
		const addedLanding = getBlock(state.blocks, addedLandingId);

		if (addIntermidiateLanding && addedLanding) {
			const blockAttachedEdge = addedLanding.edges.find(
				(eg) => eg.attachedToParent
			);

			handleAddStair({
				state,
				dispatch,
				block: addedLanding,
				edge: addedLanding.edges.find(
					(eg) => eg.label == getOppositeEdge(blockAttachedEdge?.label)
				),
			});

			setAddIntermidiateLanding(false);
			setAddedLandingId('');
		}
	}, [state.blocks, addedLandingId, addIntermidiateLanding]);

	return (
		<Box sx={{ paddingY: '10px', paddingLeft: '20px', width: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '10px',
					paddingRight: '10px',
				}}
			>
				<Button
					startIcon={<ArrowBackIcon />}
					sx={{ color: 'black', fontWeight: 500 }}
					onClick={() => setStage(0)}
				>
					Back
				</Button>

				<IconButton onClick={handleCloseDialog}>
					<CloseIcon sx={{ color: theme.palette.grey[500] }} />
				</IconButton>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'start',
					alignItems: 'start',
				}}
			>
				{addableBlocks.map((aBlock, index) => (
					<Box
						key={index}
						role="button"
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'start',
							gap: '20px',
							alignItems: 'center',
							paddingY: '10px',
							borderBottom:
								index < addableBlocks.length - 1
									? '1px solid rgba(0, 0, 0, 0.2)'
									: '',
						}}
						onClick={() => {
							if (block && edge) {
								switch (aBlock.blockType) {
									case BlockType.STAIR: {
										const result = handleAddStair({
											state,
											dispatch,
											block,
											edge,
										});

										if (result) {
											setAddIntermidiateLanding(result.addIntermediateLanding);
											setAddedStairId(result.addedStairId);
										}
										break;
									}
									case BlockType.LANDING: {
										handleAddLanding({ state, dispatch, block, edge });

										break;
									}
									case BlockType.BUMPOUT: {
										handleAddBumpout({ dispatch, block, edge });

										break;
									}
									case BlockType.DECKBOX: {
										handleAddDeckbox({ state, dispatch, block, edge });

										break;
									}
									default:
										break;
								}
							}
						}}
					>
						<Box
							sx={{
								width: '40px',
								height: '40px',
								backgroundColor: aBlock.color,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								marginBottom: '5px',
							}}
						>
							{aBlock.icon}
						</Box>

						<Typography
							sx={{ fontSize: '13px', textAlign: 'center', fontWeight: 500 }}
						>
							Add {aBlock.name}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default BlockOptions;
